import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

// Sign In / Sign Up modal — supports both email+password and one-click
// Emergent Google sign-in. Opens whenever a coach tries to save/load and
// isn't yet signed in.
export const AuthDialog = ({ open, onOpenChange, defaultTab = "signin", onBeforeRedirect }) => {
  const { login, register, sessionExpired } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const showExpired = sessionExpired && tab !== "signup";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setEmail("");
      setPassword("");
      setName("");
      setError("");
    }
  }, [open, defaultTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = tab === "signup"
      ? await register(email.trim(), password, name.trim())
      : await login(email.trim(), password);
    setSubmitting(false);
    if (result.ok) {
      toast.success(tab === "signup" ? "Account created" : "Signed in");
      onOpenChange(false, result.user);
    } else {
      setError(result.error || "Please try again");
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + "/";
    // Let the workspace save a local draft first (DI-1): this is a full-page redirect.
    if (onBeforeRedirect) onBeforeRedirect();
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => onOpenChange(v)}>
      <DialogContent data-testid="auth-dialog" className="max-w-sm">
        <DialogHeader>
          <div className="flex justify-center mb-1">
            <Logo variant="wordmark" height={22} data-testid="auth-brand-logo" />
          </div>
          <DialogTitle
            className="text-xl tracking-tight font-bold text-center"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {tab === "signup"
              ? "Create your coach account"
              : showExpired ? "Your sign-in has expired" : "Welcome back, coach"}
          </DialogTitle>
          <DialogDescription
            className="text-center"
            data-testid={showExpired ? "auth-expired-notice" : undefined}
          >
            {tab === "signup"
              ? "Save and reload your sessions from any device."
              : showExpired
                ? "Sign in again to save. Your work is still here and will not be lost."
                : "Sign in to access your saved sessions and activities."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 p-0.5 rounded-md bg-slate-100 self-center">
          <button
            type="button"
            onClick={() => setTab("signin")}
            data-testid="auth-tab-signin"
            className={
              "px-3 py-1 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors " +
              (tab === "signin"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-900")
            }
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setTab("signup")}
            data-testid="auth-tab-signup"
            className={
              "px-3 py-1 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors " +
              (tab === "signup"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-900")
            }
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {tab === "signup" && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="auth-name" className="text-xs font-semibold text-slate-700">
                Name (optional)
              </Label>
              <Input
                id="auth-name"
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Coach Marta"
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="auth-email" className="text-xs font-semibold text-slate-700">
              Email
            </Label>
            <Input
              id="auth-email"
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@club.com"
              autoComplete={tab === "signup" ? "email" : "username"}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="auth-password" className="text-xs font-semibold text-slate-700">
              Password
            </Label>
            <Input
              id="auth-password"
              data-testid="auth-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={tab === "signup" ? "At least 6 characters" : "Your password"}
              autoComplete={tab === "signup" ? "new-password" : "current-password"}
            />
          </div>

          {error && (
            <div
              data-testid="auth-error"
              className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="auth-submit-btn"
            className="mt-1"
          >
            {submitting
              ? (tab === "signup" ? "Creating account…" : "Signing in…")
              : (tab === "signup" ? "Create account" : "Sign in")}
          </Button>
        </form>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              or
            </span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleGoogle}
          data-testid="auth-google-btn"
          className="w-full"
        >
          {/* Simple G icon (no emoji, plain SVG) */}
          <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </Button>
      </DialogContent>
    </Dialog>
  );
};
